import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Gift, Copy, Share2, MessageCircle, Users, Coins, Trophy, Check, Sparkles, Award, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

function BadgeUnlocked({ badge, onClose }) {
  const { t } = useT();
  useEffect(() => {
    const colors = ["#fbbf24", "#f472b6", "#a78bfa", "#ffffff"];
    confetti({ particleCount: 180, spread: 120, startVelocity: 45, origin: { y: 0.5 }, colors, zIndex: 10000 });
  }, []);
  return createPortal(
    <motion.div data-testid="badge-unlocked-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] overflow-y-auto bg-black/85 backdrop-blur-md">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <motion.div initial={{ scale: 0.7, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="relative w-full max-w-sm rounded-3xl border border-amber-400/50 bg-[#0d0b14] p-8 text-center shadow-[0_0_80px_rgba(251,191,36,0.35)] overflow-hidden">
          <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-gradient-to-br from-amber-400/40 via-fuchsia-500/30 to-transparent blur-3xl" />
          <motion.div animate={{ rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="relative mx-auto mb-4 w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-fuchsia-400 flex items-center justify-center shadow-[0_0_40px_rgba(244,114,182,0.6)]">
            <Award className="w-10 h-10 text-[#0d0b14]" strokeWidth={2.5} />
          </motion.div>
          <p className="relative text-xs font-bold tracking-[0.3em] text-amber-300 uppercase">{t("ref_badge_unlocked")}</p>
          <h2 data-testid="badge-unlocked-title" className="relative mt-2 font-display font-black text-3xl bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-fuchsia-300">{t(`ref_badge_${badge.badge}`)}</h2>
          <p className="relative mt-2 text-sm text-gray-300">{t("ref_badge_sub").replace("{n}", badge.count)}</p>
          <p data-testid="badge-bonus-amount" className="relative mt-4 font-mono font-black text-3xl text-amber-300 flex items-center justify-center gap-2"><Coins className="w-7 h-7" /> +{Number(badge.bonus).toLocaleString()}</p>
          <p className="relative mt-1 text-[11px] text-gray-500">{t("jackpot_no_cash")}</p>
          <button data-testid="badge-unlocked-close" onClick={onClose} className="relative mt-6 w-full py-3 rounded-full font-bold text-[#0d0b14] bg-gradient-to-r from-amber-300 to-fuchsia-400 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200">{t("ref_badge_collect")}</button>
        </motion.div>
      </div>
    </motion.div>,
    document.body
  );
}

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <div data-testid={testId} className="glass rounded-2xl p-4 border border-white/10">
    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5"><Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}</p>
    <p className={`font-mono font-black text-2xl mt-1 ${accent}`}>{value}</p>
  </div>
);

export default function Invite() {
  const [me, setMe] = useState(null);
  const [top, setTop] = useState([]);
  const [copied, setCopied] = useState(false);
  const { t } = useT();
  const { refresh } = useAuth();

  useEffect(() => {
    // render as soon as the player's own data arrives; the recruiter board fills in independently
    api.get("/referrals/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((r) => setTop(r.data)).catch(() => {});
  }, []);

  if (!me) return <div className="p-8 text-gray-500">{t("loading")}</div>;

  const ackBadge = async () => {
    setMe((m) => ({ ...m, badge_pending: null }));
    try { await api.post("/referrals/badge-ack"); } catch { /* ignore */ }
    await refresh();
  };

  const link = `${window.location.origin}/?ref=${me.code}`;
  const msg = t("ref_share_msg").replace("{n}", me.rewards.friend.toLocaleString()).replace("{link}", link);
  const copy = async () => { await navigator.clipboard.writeText(link); setCopied(true); toast.success(t("ref_copied")); setTimeout(() => setCopied(false), 1500); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "PhonePlay", text: msg, url: link }); } catch { /* cancelled */ } }
    else copy();
  };

  return (
    <div data-testid="invite-page" className="p-4 lg:p-8 max-w-5xl mx-auto">
      <AnimatePresence>{me.badge_pending && <BadgeUnlocked badge={me.badge_pending} onClose={ackBadge} />}</AnimatePresence>
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-600/15 via-purple-700/10 to-transparent p-6 lg:p-8">
          <div className="pointer-events-none absolute -right-16 -top-20 w-72 h-72 rounded-full bg-fuchsia-500/20 blur-3xl" />
          <p className="text-[11px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold flex items-center gap-2"><Gift className="w-4 h-4" /> {t("ref_kicker")}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">{t("ref_title")}</h1>
          <p className="text-sm text-gray-300 mt-3 max-w-lg">
            {t("ref_sub").replace("{a}", me.rewards.friend.toLocaleString()).replace("{b}", me.rewards.referrer.toLocaleString()).replace("{c}", me.rewards.streak_bonus.toLocaleString()).replace("{d}", me.rewards.streak_after)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">{t("jackpot_no_cash")}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start">
            <div className="rounded-2xl bg-white p-3 shrink-0" data-testid="invite-qr">
              <QRCodeSVG value={link} size={132} level="M" fgColor="#0d0b14" />
            </div>
            <div className="flex-1 min-w-0 w-full">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{t("ref_your_code")}</p>
              <p data-testid="invite-code" className="font-mono font-black text-3xl text-fuchsia-200 tracking-[0.2em] mt-1">{me.code}</p>
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-black/40 border border-white/10 px-3 py-2">
                <span data-testid="invite-link" className="font-mono text-xs text-gray-300 truncate flex-1">{link}</span>
                <button data-testid="invite-copy-button" onClick={copy} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors duration-200" title={t("ref_copy")}>
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                <button data-testid="invite-share-button" onClick={share} className="py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:opacity-90 transition-opacity duration-200 flex items-center justify-center gap-1.5"><Share2 className="w-4 h-4" /> {t("ref_share")}</button>
                <a data-testid="invite-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className="py-2.5 rounded-xl font-semibold text-sm bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors duration-200 flex items-center justify-center gap-1.5"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                <a data-testid="invite-sms" href={`sms:?&body=${encodeURIComponent(msg)}`} className="py-2.5 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-200 flex items-center justify-center gap-1.5 col-span-2 sm:col-span-1"><MessageCircle className="w-4 h-4" /> SMS</a>
              </div>
            </div>
          </div>
        </motion.section>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Users} label={t("ref_invited")} value={me.invited} accent="text-cyan-300" testId="invite-stat-invited" />
            <Stat icon={Sparkles} label={t("ref_played")} value={me.rewarded} accent="text-fuchsia-300" testId="invite-stat-rewarded" />
            <Stat icon={Coins} label={t("ref_earned")} value={me.coins_earned.toLocaleString()} accent="text-amber-300" testId="invite-stat-earned" />
          </div>

          <div className="glass rounded-2xl p-5 border border-white/10" data-testid="invite-milestones">
            <h2 className="font-display font-bold text-base flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-amber-400" /> {t("ref_milestones")}</h2>
            <div className="grid grid-cols-3 gap-2">
              {me.milestones.map((m) => {
                const done = me.badges.includes(m.badge);
                const pct = Math.min(100, Math.round((me.rewarded / m.count) * 100));
                return (
                  <div key={m.badge} data-testid={`milestone-${m.badge}`} className={`rounded-xl p-3 border text-center ${done ? "border-amber-400/60 bg-amber-500/10" : "border-white/10 bg-black/30"}`}>
                    <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-gradient-to-br from-amber-300 to-fuchsia-400 text-[#0d0b14]" : "bg-white/5 text-gray-600"}`}>
                      {done ? <Award className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                    </div>
                    <p className={`text-xs font-bold mt-2 ${done ? "text-amber-200" : "text-gray-400"}`}>{t(`ref_badge_${m.badge}`)}</p>
                    <p className="text-[10px] text-gray-500">{m.count} {t("ref_friends_lower")} · +{m.bonus.toLocaleString()}</p>
                    {!done && <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-fuchsia-400 to-amber-300" style={{ width: `${pct}%` }} /></div>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="glass rounded-2xl p-5 border border-white/10">
            <h2 className="font-display font-bold text-base flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-amber-400" /> {t("ref_top")}</h2>
            {top.length === 0 ? <p className="text-sm text-gray-500">{t("ref_top_empty")}</p> : (
              <ol data-testid="invite-leaderboard" className="space-y-1.5">
                {top.map((r, i) => (
                  <li key={i} className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 ${r.you ? "bg-fuchsia-500/15 border border-fuchsia-400/40" : "bg-black/30"}`}>
                    <span className="flex items-center gap-2"><span className="font-mono text-gray-500 w-5">#{i + 1}</span> {r.name}
                      {r.badge && <span data-testid="leaderboard-badge" title={t(`ref_badge_${r.badge}`)} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300"><Award className="w-3 h-3" /> {t(`ref_badge_${r.badge}`)}</span>}
                      {r.you && <span className="text-[10px] text-fuchsia-300">({t("you")})</span>}</span>
                    <span className="font-mono text-amber-300 font-bold">{r.referrals} · +{Number(r.coins).toLocaleString()}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="glass rounded-2xl p-5 border border-white/10">
            <h2 className="font-display font-bold text-base mb-3">{t("ref_friends")}</h2>
            {me.friends.length === 0 ? <p className="text-sm text-gray-500">{t("ref_friends_empty")}</p> : (
              <ul data-testid="invite-friends" className="space-y-1.5">
                {me.friends.map((f) => (
                  <li key={f.referral_id} className="flex items-center justify-between text-sm bg-black/30 rounded-lg px-3 py-2">
                    <span className="text-gray-200">{f.friend_name || "Player"}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${f.status === "rewarded" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{f.status === "rewarded" ? t("ref_status_played") : t("ref_status_joined")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
